import React from 'react';
import ReactDOM from 'react-dom';
import strings from './Language';

// ---- helpers ---------------------------------------------------------------
function pad(n) { return String(n).padStart(2, '0'); }
function toISO(y, m, d) { return `${y}-${pad(m)}-${pad(d)}`; }
function parseISO(v) {
    if (!v || typeof v !== 'string') return null;
    const p = v.split('-');
    if (p.length !== 3) return null;
    const y = parseInt(p[0], 10), m = parseInt(p[1], 10), d = parseInt(p[2], 10);
    if (!y || !m || !d) return null;
    return { y, m, d };
}

function isZh() { return strings.getLanguage() === 'zh'; }

function formatDisplay(value) {
    const p = parseISO(value);
    if (!p) return '';
    if (isZh()) return `${p.y}年${p.m}月${p.d}日`;
    return `${p.y}-${pad(p.m)}-${pad(p.d)}`; // English: YYYY-MM-DD
}

function defaultHint() { return isZh() ? '年/月/日' : 'YYYY-MM-DD'; }

const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
const WEEK_EN = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const WEEK_ZH = ['日', '一', '二', '三', '四', '五', '六'];

function monthTitle(y, m) { // m is 1-12
    return isZh() ? `${y}年${m}月` : `${MONTHS_EN[m - 1]} ${y}`;
}

// ---- component -------------------------------------------------------------
/**
 * In-app date picker. Renders a styled field; tapping it opens a themed calendar
 * modal (works in any browser/webview, unlike the native <input type="date">).
 * Displays the value in the APP language. Drop-in API compatible with the old
 * native field:  props = { value:"YYYY-MM-DD", min, max, onChange, placeholder }
 * onChange is called as onChange({ target: { value } }).
 */
class NativeDateField extends React.Component {
    constructor(props) {
        super(props);
        const sel = parseISO(props.value);
        const now = new Date();
        this.state = {
            open: false,
            viewY: sel ? sel.y : now.getFullYear(),
            viewM: sel ? sel.m : now.getMonth() + 1, // 1-12
        };
    }

    openCal = () => {
        const sel = parseISO(this.props.value);
        const now = new Date();
        this.setState({
            open: true,
            viewY: sel ? sel.y : now.getFullYear(),
            viewM: sel ? sel.m : now.getMonth() + 1,
        });
    };

    close = () => this.setState({ open: false });

    prevMonth = () => this.setState((s) => {
        let m = s.viewM - 1, y = s.viewY;
        if (m < 1) { m = 12; y -= 1; }
        return { viewM: m, viewY: y };
    });

    nextMonth = () => this.setState((s) => {
        let m = s.viewM + 1, y = s.viewY;
        if (m > 12) { m = 1; y += 1; }
        return { viewM: m, viewY: y };
    });

    emit = (value) => {
        if (this.props.onChange) this.props.onChange({ target: { value } });
    };

    pick = (day) => {
        const value = toISO(this.state.viewY, this.state.viewM, day);
        this.emit(value);
        this.close();
    };

    clear = () => { this.emit(''); this.close(); };

    isDisabled = (day) => {
        const iso = toISO(this.state.viewY, this.state.viewM, day);
        const { min, max } = this.props;
        if (min && iso < min) return true;
        if (max && iso > max) return true;
        return false;
    };

    render() {
        const { value, placeholder } = this.props;
        const { open, viewY, viewM } = this.state;
        const hasValue = !!parseISO(value);
        const display = hasValue ? formatDisplay(value) : (placeholder || defaultHint());
        const sel = parseISO(value);

        const firstWeekday = new Date(viewY, viewM - 1, 1).getDay(); // 0=Sun
        const daysInMonth = new Date(viewY, viewM, 0).getDate();
        const cells = [];
        for (let i = 0; i < firstWeekday; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);

        const week = isZh() ? WEEK_ZH : WEEK_EN;

        const modal = open ? (
            <div className="idp-overlay" onClick={(e) => { e.stopPropagation(); this.close(); }}>
                <div className="idp-card" onClick={(e) => e.stopPropagation()}>
                    <div className="idp-header">
                        <button type="button" className="idp-nav" onClick={this.prevMonth}>‹</button>
                        <div className="idp-title">{monthTitle(viewY, viewM)}</div>
                        <button type="button" className="idp-nav" onClick={this.nextMonth}>›</button>
                    </div>
                    <div className="idp-week">
                        {week.map((w) => <span key={w}>{w}</span>)}
                    </div>
                    <div className="idp-grid">
                        {cells.map((d, i) => {
                            if (d === null) return <span key={"e" + i} className="idp-empty" />;
                            const isSel = sel && sel.y === viewY && sel.m === viewM && sel.d === d;
                            const disabled = this.isDisabled(d);
                            return (
                                <button
                                    key={d}
                                    type="button"
                                    className={"idp-day" + (isSel ? " sel" : "") + (disabled ? " disabled" : "")}
                                    disabled={disabled}
                                    onClick={() => this.pick(d)}
                                >
                                    {d}
                                </button>
                            );
                        })}
                    </div>
                    <div className="idp-actions">
                        <button type="button" className="idp-btn" onClick={this.clear}>{strings.clear_date || 'Clear'}</button>
                        <button type="button" className="idp-btn primary" onClick={this.close}>{strings.done || 'Done'}</button>
                    </div>
                </div>
            </div>
        ) : null;

        return (
            <div className="idp-field" onClick={this.openCal}>
                <div className={"idp-display" + (hasValue ? "" : " placeholder")}>{display}</div>
                <span className="idp-icon" role="img" aria-label="calendar">📅</span>
                {/* Render the modal at document.body so a transformed ancestor
                    (e.g. the swipeable tabs) can't clip or mis-position it. */}
                {open && typeof document !== 'undefined'
                    ? ReactDOM.createPortal(modal, document.body)
                    : null}
            </div>
        );
    }
}

export default NativeDateField;
